import { useEffect, useMemo, useState } from 'react';
import pricingService from '../services/pricingService';
import { BillingCycle, MarketingPlan } from '../types/pricing';
import {
  BILLING_CYCLES,
  footerPricingSummary,
  lowestPriceAcrossPlans,
  lowestPriceForPlan,
} from '../utils/pricingUtils';

const DEFAULT_TRIAL_DAYS = 30;

let cachedPlans: MarketingPlan[] | null = null;
let cachedTrialDays: number | null = null;
let cachePromise: Promise<{ plans: MarketingPlan[]; trialDurationDays: number }> | null = null;

function fetchPlansCached(): Promise<{ plans: MarketingPlan[]; trialDurationDays: number }> {
  if (cachedPlans && cachedTrialDays != null) {
    return Promise.resolve({ plans: cachedPlans, trialDurationDays: cachedTrialDays });
  }
  if (!cachePromise) {
    cachePromise = pricingService
      .fetchPlans()
      .then((res) => {
        cachedPlans = res.plans || [];
        const days = Number(res.trial_duration_days);
        cachedTrialDays = Number.isFinite(days) && days > 0 ? days : DEFAULT_TRIAL_DAYS;
        return { plans: cachedPlans, trialDurationDays: cachedTrialDays };
      })
      .catch((err) => {
        cachePromise = null;
        throw err;
      });
  }
  return cachePromise;
}

export function useMarketingPlans() {
  const [plans, setPlans] = useState<MarketingPlan[]>(cachedPlans || []);
  const [trialDurationDays, setTrialDurationDays] = useState(cachedTrialDays ?? DEFAULT_TRIAL_DAYS);
  const [loading, setLoading] = useState(!cachedPlans);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPlansCached()
      .then((data) => {
        if (!cancelled) {
          setPlans(data.plans);
          setTrialDurationDays(data.trialDurationDays);
          setError(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Unable to load pricing right now.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const availableCycles = useMemo(() => {
    const found = new Set<string>();
    plans.forEach((plan) =>
      plan.tiers?.forEach((tier) =>
        tier.pricing?.forEach((p) => found.add(p.billingCycle))
      )
    );
    return BILLING_CYCLES.filter((c) => found.has(c.key));
  }, [plans]);

  const lowestMonthly = useMemo(
    () => lowestPriceAcrossPlans(plans, 'monthly'),
    [plans]
  );

  const pricingSummary = useMemo(() => footerPricingSummary(plans), [plans]);

  return {
    plans,
    loading,
    error,
    trialDurationDays,
    availableCycles,
    lowestMonthly,
    pricingSummary,
    lowestPriceForPlan: (plan: MarketingPlan, cycle: BillingCycle = 'monthly') =>
      lowestPriceForPlan(plan, cycle),
  };
}
